// ─────────────────────────────────────────────
//  personSegment — "is this piece of a cell a PERSON?" — asked once.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  ⚠️ WHY THIS IS ITS OWN LEAF MODULE. The sheet spells "two people in one
//  cell" four ways — `orient`, a comma, a slash, a trailing parenthesis — and
//  until now only three of them asked whether both halves looked like people.
//  The `orient` path did not, and on 2026-08-20 the live board carried
//
//      WC7   "Olivia Meitzner 3 Orient to OB floor"
//
//  which split into a colleague named **`to OB floor`**. That string passed
//  `isPlausibleCrnaName`, passed `partitionNewStaff`, and the unattended
//  nightly job would have created a permanent staff document for it. The
//  variant `Orient to CV5` is worse: it yields `to CV` carrying shift code
//  `5`, so a routing note becomes a person with a departure time who counts
//  as cover.
//
//  The gate existed. The orient branch walked around it. So the gate now lives
//  in a module with NO dependency on either parser, and every path imports it:
//  domain/orientation, domain/providerCell, features/sheet-import/cellReader.
//
//  ⚠️ IT FAILS CLOSED AND IT EXPLAINS ITSELF. `personSegmentIssue` returns the
//  REASON a side was refused, not just a boolean, because the refusal has to
//  reach a human as a review item ("this cell named somebody I could not
//  read") rather than vanish. A shape this module has never seen becomes a
//  question, never a guess and never a new colleague.
//
//  Pure: no Firebase, no React. Text in, verdict out.
// ─────────────────────────────────────────────
import { SHIFT_CODES } from "./schedule";

const SHIFT_CODE_SET = new Set(SHIFT_CODES.map((c) => c.toUpperCase()));

// "7a-7p", "11p-7a" — the range spellings the sheet uses beyond the canonical
// list. Anchored: a whole token, not a fragment of one.
const SHIFT_RANGE_TOKEN_RE = /^\d{1,2}[ap]-\d{1,2}[ap]$/i;
const SHIFT_LSUFFIX_TOKEN_RE = /^\d+L$/i;

// ⚠️ A SURNAME ABBREVIATED TO ITS INITIAL IS STILL A PERSON, and refusing it
// dropped four trainees off the live board. The Assignment Sheet writes every
// orientee that way -- "Kayla G (2)", "Kaylene L (1)", "Adam B",
// "Jodi-Ann R 5" -- and each failed the two-character floor below.
//
// ⚠️ LAST WORD ONLY, AND NEVER THE ONLY WORD. A lone letter names nobody, and
// a LEADING initial is the match canonicalPerson deliberately refuses ("J.
// Larson" was accepted as Joshua in an early draft and removed): with three
// Bennetts and two Johns on file, a first initial is the least safe thing in
// this roster to match on. A trailing one is different -- it still carries a
// whole given name in front of it, which is what makes the resolver able to
// demand a unique fit.
const INITIAL_TOKEN_RE = /^[A-Za-z]\.?$/;

// One token of a human name. Letters plus the punctuation real surnames here
// carry — "Abd-El-Barr", "O'Brien". Two characters minimum, because "Fernanda
// Sa" is a CRNA on this board and a one-letter initial is not a name.
const NAME_TOKEN_RE = /^[A-Za-z][A-Za-z'’-]+$/;

// Words that say WHERE or WHAT, not WHO. Their presence anywhere in a side is
// enough to refuse it: no real name on this board contains one.
const ROUTING_TOKEN_RE = /^(to|from|fr|via|w|with|and|or|amp|req|orient|orients|orienting|relief|call|late|early|off|out)$/i;

// ⚠️ PLACE WORDS, BECAUSE "to" IS NOT THE ONLY WAY THE NOTE IS WRITTEN.
// `Orient to OB floor` is caught by `to`; `Orient w/ OB floor` is not, and it
// yields `OB floor` — two letters-only tokens, one of them lower-case, which
// every other clause here accepts. These are the destinations this department
// actually writes after "orient"; none of them is a surname on the live
// roster, and refusing one costs a review item while accepting one mints a
// person.
const PLACE_TOKEN_RE = /^(floor|unit|suite|lab|labs|room|rooms|holding|recovery|preop|pre-op|postop|post-op|desk|office|board|hall|ward|pacu|icu|nicu|picu|ccu|ed|er|endo|ob|obs|acutecare|cpru|flex|tbd)$/i;

// Annotation words the sheet writes AFTER a name. Bookkeeping, never a name.
// ⚠️ "Early", "Holiday" and "Hawk" are also real surnames on this board, so
// these are only ever stripped from the END and never below a two-word floor.
const ANNOTATION_WORD_RE = /^(req|reqs|relief|orientation|interview|late|early|off|out|holiday|vacation|pto|call|nh|at)$/i;

// Generator residue: the QGenda block name leaks its campus/service tag onto
// the rendered cell ("AT-APP-7p-7a OB" → "Femi John 7p-7a OB"). It is not a
// name word and not a room — the person is already IN room OB.
const BLOCK_SUFFIX_RE = /^(OB|NH|CS|LD|MISC)$/;

// A professional credential written after a name — "Kayla Gibbons, CRNA". Part
// of how the person is addressed, never part of how they are matched.
const CREDENTIAL_RE = /\s*,?\s*\b(crna|crna-?a|aa|srna|rn|md|do|np|pa|pa-?c)\.?\s*$/i;

const str = (v) => String(v ?? "").trim();

/** Cells that name nobody. The sheet uses these as "this seat is empty". */
const PLACEHOLDER_RE = /^(x{2,}|tbd|tba|open|none|n\/?a|vacant|empty|-{1,}|\?+)$/i;

/** True when a segment names nobody at all. */
export const isEmptySegment = (segment) => {
  const s = str(segment);
  return !s || PLACEHOLDER_RE.test(s);
};

/** Does this single token read as somebody's shift code rather than their name? */
export function isShiftToken(token) {
  const t = str(token).toUpperCase();
  if (!t) return false;
  return (
    SHIFT_CODE_SET.has(t) ||
    SHIFT_RANGE_TOKEN_RE.test(t) ||
    SHIFT_LSUFFIX_TOKEN_RE.test(t) ||
    /^\d{1,2}$/.test(t)
  );
}

/** Does this single token read as a WORD OF A NAME? */
export const isNameToken = (token) => NAME_TOKEN_RE.test(str(token));

/** Is this token a direction/bookkeeping word rather than part of a name? */
export const isRoutingToken = (token) => ROUTING_TOKEN_RE.test(str(token));

/** Is this token a PLACE the sheet routes somebody to? */
export const isPlaceToken = (token) => PLACE_TOKEN_RE.test(str(token));

// ── A ROUTING WORD WITH NOTHING AFTER IT ─────────────
//
// ⚠️ THE CELL THAT COST A REAL CRNA HER ROOM AND LOST HER TRAINEE ENTIRELY.
// The sheet wrote, 2026-08-24, WC IR:
//
//     maria mckay 5 to orient adam batz
//
// `ORIENT_RE` split it correctly into "maria mckay 5 to" and "adam batz" — and
// then `personSegmentIssue` refused the first half because it contains the
// routing word "to", so `parseOrientation` returned null, the pair collapsed,
// Maria was stored as the raw string "Maria McKay 5 to" with NO id (unlinked,
// unmessageable, no breaks) and **Adam Batz vanished from the board entirely**.
//
// The routing refusal is right when the word points somewhere —
// "Victoria Gordeen 3 to/from CIE6" must never split into people. It is wrong
// when the word DANGLES with nothing after it, which is just how this
// department writes "X, then hand over": the name in front of it is a perfectly
// ordinary name.
//
// ⚠️ SO: STRIP ONLY AT THE END, AND ONLY WHOLE TOKENS. A routing word followed
// by anything at all is left exactly where it is, so every cell that fails
// closed today still does.
export function stripDanglingRoutingWords(segment) {
  const tokens = str(segment).split(/\s+/).filter(Boolean);
  while (tokens.length && isRoutingToken(tokens[tokens.length - 1])) tokens.pop();
  return tokens.join(" ");
}

// ── A ROUTING ANNOTATION IS NOT PART OF A NAME ───────────
//
// "to CT", "from IR", "fr TEE", "to/from CIE6" — where this person is going,
// never who they are. It lived in the sheet reader alone, which is how
// `orientationParts` came to judge "is this a person?" against text that still
// carried one: on 2026-09-07 the live board wrote
//
//     "Rebecca Sigler 3 to TEE/Orient Cheryl Martin"
//
// and the preceptor half was refused for containing the routing word "to", so
// the pair collapsed, Rebecca was stored as the whole sentence with no id, and
// **Cheryl Martin was on no screen at all**. Same mechanism as the maria mckay
// cell above, one layer further out. So the strip lives HERE and both the
// person test and the sheet reader call it.
//
// ⚠️ APPLIED REPEATEDLY, BECAUSE THE SHEET WRITES A JOURNEY.
// "Rebecca S 3 from MRI then to TEE" carries two annotations joined by a
// connective; stripping only the last one left the name "Rebecca S 3 from MRI
// then".
//
// ⚠️ ANCHORED AT THE END AND REQUIRING WHITESPACE IN FRONT, which is what
// keeps every refusal that already works. "Orient to OB floor" yields the
// orientee segment "to OB floor" — the direction word is at position zero,
// nothing matches, and it stays refused rather than becoming a colleague.
const ROUTING_ANN_RE =
  /\s+(to|from|fr)(?:\s*\/\s*(to|from|fr))?\.?\s+([A-Za-z]{1,6}\s?\d{0,2})\s*$/i;

// The words this department joins two legs of one journey with.
const CONNECTIVE_TAIL_RE = /[\s,]+(?:then|and|&)\s*$/i;

/**
 * Take the routing annotations off a segment and say where they pointed.
 *
 * The FIRST direction of each kind wins: the annotations are read right to
 * left, and the room nearest the name is the one the sheet meant.
 *
 * @returns {{text: string, toRoom: string, fromRoom: string}}
 */
export function stripRoutingNote(segment) {
  let text = str(segment);
  let toRoom = "";
  let fromRoom = "";
  for (;;) {
    const ann = text.match(ROUTING_ANN_RE);
    if (!ann) break;
    const dirs = [ann[1], ann[2]].filter(Boolean).map((d) => d.toLowerCase());
    const target = ann[3].trim().toUpperCase();
    if (dirs.includes("to")) toRoom = target;
    if (dirs.includes("from") || dirs.includes("fr")) fromRoom = target;
    const head = text.slice(0, ann.index).replace(CONNECTIVE_TAIL_RE, "").trim();
    // Never hand back nothing: a segment that is ONLY a routing note keeps its
    // text so a human sees the cell rather than a blank seat.
    if (!head) break;
    text = head;
  }
  return { text, toRoom, fromRoom };
}

// ── "MOORE, SHANNON" IS SHANNON MOORE ────────────────────
//
// ⚠️ SURNAME-FIRST IS A THIRD MEANING FOR THE COMMA. The sheet already uses it
// to separate two people ("Jim Slone 3, Gabi Swanson 9") and to hold a stray
// ("Sarabel Mercado, Torres 3"). Left as written, "Moore, Shannon 5" matches no
// staff record — the comma is not a name character — and the importer offers to
// create a SECOND Shannon Moore beside the real one.
//
// ⚠️ ONE TOKEN EITHER SIDE, WHICH IS WHAT MAKES IT UNAMBIGUOUS. Two people are
// written with a full name before the comma, so requiring a single word on the
// left leaves that split untouched, and requiring a single word on the right
// leaves "Sarabel Mercado, Torres 3" alone.
const SURNAME_FIRST_RE = /^([A-Za-z][A-Za-z'’-]+),\s+([A-Za-z][A-Za-z'’-]+)((?:\s+\S+)?)\s*$/;

/** "Moore, Shannon 5" → "Shannon Moore 5". Anything else is returned as-is. */
export function flipSurnameFirst(segment) {
  const s = str(segment);
  const m = s.match(SURNAME_FIRST_RE);
  if (!m) return s;
  const tail = m[3].trim();
  // A trailing word is only kept when it is that person's shift. Anything else
  // is a shape this rule has not been shown, so the cell is left as written.
  if (tail && !isShiftToken(tail)) return s;
  return [m[2], m[1], tail].filter(Boolean).join(" ");
}

/** Is this token an annotation the sheet appends after a name? */
export const isAnnotationWord = (token) => ANNOTATION_WORD_RE.test(str(token));

// ── A TRAILING CLOCK TIME IS NOT A SHIFT CODE ────────────
//
// ⚠️ THE FAILURE THIS EXISTS TO STOP: A REAL PERSON UNDER A BROKEN NAME.
// "Ashley Weider 5 orient Lester 7-8:30" reached the importer's trailing
// bare-number rule, whose `^(.*?)[\s-]*(\d+)\s*$` is happy to bite into the
// MIDDLE of a token: it took "30" as Lester's shift code and left his name as
// **"Lester 7-8:"** — written to Firestore as `orienteeName` and rendered on
// the bar, the roster, the Breaks panel and the spoken label. A missing id is
// not an absent person; a mangled name is that same failure in other clothes.
//
// ⚠️ NO DEPARTURE WAS EVER FABRICATED — `shiftCodeEndHour24("30")` is null —
// so the dangerous half was already safe. This fixes the visible half, and
// deliberately records NO shift: a time this app cannot interpret must never
// become a leaving time.
//
// The shift vocabulary is am/pm-marked ("7a-7p", "11p - 7a"), L-suffixed
// ("3L"), A-start ("7A"), or a bare afternoon number ("5"). A bare digit range
// carrying a colon is none of those.
//
// ⚠️ EVERY CLAUSE REFUSES SOMETHING REAL. Measured against the two board
// exports and the recorded cells on 2026-08-20, this leaves untouched:
//     "Anthony Bullock 7a-11p"  am/pm range — a real shift code
//     "Femi John 7a-7a"         the 24h on-call spelling
//     "Bobby Fry 3L", "Ann Ray 7A", "Grace Elaro 24"
//     "Chris Glenn 7 (7a-3p)"   parenthesised — does not END in a digit
//     "Taylor Schmitt 3 TOR16"  a room; the routing rule owns it
//     "Name-5"                  the documented hyphen-shift spelling — the
//                               range needs a DIGIT in front of its dash
// A separator before the number is required, so a cell that is ONLY a time
// keeps its text: better an odd name than an empty one.
//
// The optional lead-in is the one word this sheet actually puts in front of a
// clock. The general annotation list is ANNOTATION_WORD_RE above; it is not reused
// here because none of its other words precede a time on this board, and a
// wider rule would only make the strip more eager than the evidence supports.
const TIME_NOTE_RE =
  /(?:\s+orient(?:ation|s|ing)?)?[\s-]+\d{1,2}(?::\d{2})?\s*[-–—]\s*\d{1,2}(?::\d{2})?\s*$|(?:\s+orient(?:ation|s|ing)?)?\s+\d{1,2}:\d{2}\s*$/i;

/**
 * Drop a trailing clock-time note, KEEPING that person's shift token in place.
 *
 * ⚠️ EXPORTED FOR THE IMPORTER, which cannot call `cleanProviderName`: the
 * importer must keep the trailing shift number so it can parse it, and
 * `cleanProviderName` deletes it. Two definitions of "what a time note looks
 * like" is the shape this repo keeps getting burned by, so there is one, here,
 * and `parseCrnaCell` calls it.
 */
export function stripTimeNote(segment) {
  const s = String(segment || "").trim();
  if (!s) return "";
  const stripped = s.replace(TIME_NOTE_RE, "").trim();
  // Never hand back nothing.
  return stripped || s;
}

// ─────────────────────────────────────────────
//  normalizeSegment — put ONE person's segment into the shape the shift rules
//  can read, and say what was taken off.
//
//  ⚠️ THIS IS THE FIX FOR "THE CELL DOES NOT END IN A SHIFT TOKEN". Measured
//  on the live sheet 2026-08-20, five of fifty-nine cells lost their shift —
//  and with it the roster entry, the id and the person — for one of three
//  reasons, none of them about the name:
//
//      "Bob Schneider 7 (REQ)"                   a parenthesised annotation
//      "Kirsten Bowman 7 OB/ Femi John 7p-7a OB" a QGenda block-name suffix
//      "Taylor Prentice/Maria McKay 11a -7a"     a SPACE inside the range
//
//  Each removal is recorded as a note, so nothing is silently discarded.
// ─────────────────────────────────────────────
export function normalizeSegment(segment) {
  const notes = [];
  let s = str(segment);
  if (!s) return { text: "", notes };

  // 1. A space inside an am/pm window. "11a -7a" and "11a - 7a" are the same
  //    shift as "11a-7a"; only the anchored regex disagreed.
  s = s.replace(/\b(\d{1,2})\s*([ap])\s*-\s*(\d{1,2})\s*([ap])\b/gi, (_m, a, b, c, d) => `${a}${b}-${c}${d}`);

  // 1b. A trailing credential — ", CRNA". Dropped AFTER the window collapse so
  //     a shift is never mistaken for one, and recorded rather than lost.
  const cred = s.match(CREDENTIAL_RE);
  if (cred && s.replace(CREDENTIAL_RE, "").trim()) {
    notes.push({ code: "credential", text: cred[1] });
    s = s.replace(CREDENTIAL_RE, "").trim();
  }

  // 1c. A clock note is not a shift code. "Lester 7-8:30" is one person and a
  //     time this app cannot interpret; leaving it on made "Lester 7-8:" a name
  //     and "30" a departure. Stripped HERE so the PERSON TEST sees it too — it
  //     used to run only inside the sheet parser, so the gate that decides "is
  //     this a person" was shown text the parser would never keep, and refused
  //     a real orientee.
  s = stripTimeNote(s);

  // 2. A trailing parenthesis holding an ANNOTATION, not a person.
  //    "(REQ)" / "(req)" / "(relief)". A parenthesised PERSON is a co-provider
  //    and is split off upstream, so anything reaching here is bookkeeping.
  const paren = s.match(/^(.*\S)\s*\(([^()]+)\)\s*$/);
  const parenBody = paren ? paren[2].trim() : "";
  // ⚠️ A BARE NUMBER IN BRACKETS IS THE ORIENTEE'S ROTATION COUNTER, not a
  //    shift and not a name. "Kayla G (1)" and "Kayla G (2)" are ONE trainee
  //    working with two preceptors across the day; left on, the counter made
  //    her two different people and neither of them name-shaped.
  //    The seat holder's own "(REQ)" annotation is unchanged, and a
  //    parenthesised PERSON is split off upstream long before this.
  if (paren && (isAnnotationWord(parenBody) || /^\d{1,2}$/.test(parenBody))) {
    notes.push({ code: isAnnotationWord(parenBody) ? "annotation" : "sequence", text: parenBody });
    s = paren[1].trim();
  }

  // 3. Trailing bookkeeping words: "… 7 REQ", "… 3 Interview at".
  //    Floor of two tokens — "Early" and "Holiday" are surnames.
  let tokens = s.split(/\s+/).filter(Boolean);
  while (tokens.length > 2 && isAnnotationWord(tokens[tokens.length - 1])) {
    notes.push({ code: "annotation", text: tokens[tokens.length - 1] });
    tokens = tokens.slice(0, -1);
  }

  // 4. A QGenda block-name suffix riding after the shift: "… 7 OB".
  //    Required to FOLLOW a shift token, which is what makes it generator
  //    residue rather than a word somebody typed.
  if (
    tokens.length > 2 &&
    BLOCK_SUFFIX_RE.test(tokens[tokens.length - 1]) &&
    isShiftToken(tokens[tokens.length - 2])
  ) {
    notes.push({ code: "blockSuffix", text: tokens[tokens.length - 1] });
    tokens = tokens.slice(0, -1);
  }

  return { text: tokens.join(" "), notes };
}

/**
 * WHY this segment is not a person — or `null` when it is one.
 *
 * @param {string} text
 * @param {{minWords?: number}} [opts]  minWords 1 allows a first-name-only
 *   orientee ("Fernanda Sa 3 orient Olivia"), which the sheet really writes.
 * @returns {null | {code: string, detail?: string}}
 */
export function personSegmentIssue(text, opts = {}) {
  const minWords = opts.minWords ?? 2;
  const s = str(text);
  if (!s) return { code: "empty" };
  if (isEmptySegment(s)) return { code: "placeholder", detail: s };

  const { text: body } = normalizeSegment(s);
  let tokens = body.split(/\s+/).filter(Boolean);
  if (!tokens.length) return { code: "empty" };

  // A direction word — "…3 to", "from CIE6". Never part of a person.
  const routing = tokens.find(isRoutingToken);
  if (routing) return { code: "routingWord", detail: routing };
  // A place — "OB floor", "to PACU". The destination of a note, not a name.
  const place = tokens.find(isPlaceToken);
  if (place) return { code: "placeWord", detail: place };

  // Their OWN shift may trail the name — exactly one token, and only when a
  // name would still be left in front of it ("TOR 16" must not become "TOR").
  if (tokens.length > 1 && isShiftToken(tokens[tokens.length - 1])) tokens = tokens.slice(0, -1);

  if (tokens.length < minWords) return { code: "tooFewNameWords", detail: tokens.join(" ") };

  const notName = tokens.find((t, i) => {
    if (isNameToken(t)) return false;
    // The abbreviated surname: last position, and something in front of it.
    return !(i > 0 && i === tokens.length - 1 && INITIAL_TOKEN_RE.test(t));
  });
  if (notName) return { code: "notNameShaped", detail: notName };

  // ⚠️ "CAR MRI" is two letters-only words. Room codes on this sheet are typed
  // in CAPS and people in title case, so a side with no lower-case letter at
  // all is a location, not a colleague. This can only ever make a split LESS
  // likely, which is the direction to be wrong in.
  if (!tokens.some((t) => t !== t.toUpperCase())) return { code: "allCaps", detail: tokens.join(" ") };

  return null;
}

/** Is this segment shaped like ONE PERSON — a name, optionally plus their shift? */
export const isPersonSegment = (text, opts) => personSegmentIssue(text, opts) === null;
